/**
 * AuthService — JWT bearer session for the NEW /api surface.
 *
 * Replaces the PHPSESSID cookie the composer used against the legacy PHP
 * entrypoints (do_login_json.php / session_json.php / do_logout.php). Every
 * /api call carries `Authorization: Bearer <access token>`; a 401 triggers ONE
 * single-flight refresh (POST /api/auth/refresh) and a single retry.
 *
 * PERSISTENCE: the app already keeps its session in localStorage under the key
 * 'userdata' (written by RootPage.loginOK / pullSessionFromServer, read by
 * RootPage.checkExistingSession and TopBar.componentDidMount). The tokens are
 * stored as SIBLINGS of `loginResponse` inside that same blob, never inside it:
 * `loginResponse` is console.log'd in several places and is what the rest of the
 * app hands around. Tokens are never logged.
 */
import Config from '../Config.jsx';

const STORAGE_KEY = 'userdata';

// "This browser holds an EdCity SSO session." Kept OUTSIDE the session store on
// purpose: clearSession() runs on every 401 and on every logout, and this flag
// has to outlive our session — it describes a cookie on EdCity's origin that
// only EdCity can clear, and it is read at the moment our own session is being
// torn down. Set when a round trip to EdCity STARTS (not when it succeeds): a
// refused login still leaves the user authenticated at EdCity, and that is the
// case where a silent re-login is most confusing.
const EDCITY_SSO_KEY = 'ev_edcity_sso';

let accessToken = null;
let refreshToken = null;
let primed = false;
let refreshInFlight = null;
let sessionExpiredHandler = null;

// ---------------------------------------------------------------- url helper

/** Join Config.API_PATH() (which ends in "/") with an api-relative path. */
export function apiUrl(path) {
  const clean = String(path || "").replace(/^\/+/, "");
  // Local CRA: same-origin `/api/...`. setupProxy.js forwards /api to the
  // live host, so login does not depend on production CORS allowing localhost.
  // HOST_URL / API_PATH stay absolute — sharing, epack, and <img> URLs still
  // point at the real hosts.
  if (Config.IS_DEBUG() === true
      && typeof window !== 'undefined'
      && (window.location.hostname === 'localhost'
          || window.location.hostname === '127.0.0.1')) {
    return '/' + clean;
  }
  const base = Config.API_PATH();
  return base.endsWith("/") ? base + clean : base + "/" + clean;
}

// ------------------------------------------------------------------- storage

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeStore(patch) {
  const next = Object.assign({}, readStore() || {}, patch);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    console.error('AuthService: failed to persist session', e);
  }
  return next;
}

function prime() {
  if (primed) {
    return;
  }
  primed = true;
  const stored = readStore();
  if (stored) {
    accessToken = stored.accessToken || null;
    refreshToken = stored.refreshToken || null;
  }
}

// Load the stored tokens eagerly: TopBar.logoutClicked removes the 'userdata'
// key synchronously while the logout request is still in flight, so the in
// memory copy has to exist before that happens.
prime();

/** Seconds-since-epoch `exp` of a JWT, or null when it cannot be read. */
function tokenExpiryMs(token) {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) {
      return null;
    }
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    return (typeof claims.exp === 'number') ? claims.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

// --------------------------------------------------------------------- state

export function getAccessToken() {
  prime();
  return accessToken;
}

export function getRefreshToken() {
  prime();
  return refreshToken;
}

export function isLoggedIn() {
  return Boolean(getAccessToken());
}

export function setTokens(newAccessToken, newRefreshToken) {
  prime();
  if (newAccessToken) {
    accessToken = newAccessToken;
  }
  if (newRefreshToken) {
    refreshToken = newRefreshToken;
  }
  writeStore({ accessToken: accessToken, refreshToken: refreshToken });
}

export function clearSession() {
  prime();
  accessToken = null;
  refreshToken = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Persist a login/session-restore result the way RootPage always has, but with
 * the tokens merged in and the expiry driven by the refresh token's own `exp`
 * instead of an invented window. `rememberMe` still caps how long the stored
 * session is honoured (2h vs 7d), matching the pre-migration behaviour.
 */
export function persistSession(loginResponse, rememberMe = false) {
  const now = Date.now();
  const cap = now + (rememberMe ? 24 * 7 : 2) * 60 * 60 * 1000;
  const refreshExp = tokenExpiryMs(getRefreshToken());
  const expiresAt = refreshExp ? Math.min(cap, refreshExp) : cap;
  return writeStore({
    loginResponse: loginResponse,
    expiresAt: expiresAt,
    rememberMe: rememberMe,
    createdAt: now,
    accessToken: accessToken,
    refreshToken: refreshToken,
  });
}

/**
 * Called when the refresh token is gone or rejected — i.e. the session is over
 * and no retry can save it. RootPage registers a handler that bounces the user
 * back to the login screen instead of leaving a half-dead page.
 */
export function setSessionExpiredHandler(fn) {
  sessionExpiredHandler = (typeof fn === 'function') ? fn : null;
}

function notifySessionExpired() {
  if (sessionExpiredHandler) {
    try {
      sessionExpiredHandler();
    } catch (e) {
      console.error('AuthService: session-expired handler threw', e);
    }
  }
}

// Non-zero while a brand-new session is being minted from an SSO ticket. See
// endSession() for why a counter and not a boolean.
let mintDepth = 0;

/**
 * Run `mint` with the "your session is over" machinery muted.
 *
 * A sign-in that arrives by REDIRECT lands in a tab that is already running: on
 * the EdCity return leg RootPage paints the app shell immediately (it must, or
 * the user stares at a login form during the exchange), so LocaleViewerPage and
 * TopBar have already dispatched requests carrying the OUTGOING session's bearer
 * by the time the exchange starts. Those requests are doomed and their 401s are
 * meaningless — but the ordinary 401 path reads them as proof that the session
 * is dead, and clearSession() there would destroy the pair this exchange is
 * about to store while notifySessionExpired() throws up a "please sign in again"
 * toast over a sign-in that just succeeded.
 *
 * A counter rather than a flag because nothing stops two mints overlapping (a
 * double-clicked provider button, a re-entered dialog); the guard must stay up
 * until the LAST one finishes.
 */
async function whileMinting(mint) {
  mintDepth += 1;
  try {
    return await mint();
  } finally {
    mintDepth -= 1;
  }
}

/**
 * The session is over: drop the stored pair and send the user back to the login
 * screen. The single terminal for every "no retry can save this" path, so the
 * mint guard has exactly one place to sit.
 */
function endSession() {
  if (mintDepth > 0) {
    // Muted, not deferred: a 401 from the session being replaced says nothing
    // about the one being minted. The individual request still fails normally.
    console.warn('AuthService: ignoring a 401 from the outgoing session while a new one is being minted');
    return;
  }
  clearSession();
  notifySessionExpired();
}

// ------------------------------------------------------------------ requests

/** Build the synthetic login body the app's callbacks already expect. */
function buildLoginBody(data) {
  const userInfo = (data && data.user_info) ? data.user_info : {};
  return {
    // Legacy do_login_json.php reported success with login_result > 0; every
    // caller (LoginCreateSession, LoginPage.loginOK) still tests that field.
    login_result: 1,
    user_info: userInfo,
    school_info: (data && data.school_info) ? data.school_info : {},
    // LoginPage.loginOK reads has_read_tac off the TOP level of the body; the
    // new surface nests it inside user_info.
    has_read_tac: userInfo.has_read_tac,
  };
}

/**
 * A superagent-shaped response object. The app reads `res.body` everywhere and
 * `res.text` in TopBar.componentDidMount, so both are provided.
 */
export function makeResponse(status, body) {
  return {
    status: status,
    ok: status >= 200 && status < 300,
    body: body,
    text: (() => {
      try {
        return JSON.stringify(body);
      } catch (e) {
        return "";
      }
    })(),
  };
}

export async function login(username, password) {
  const resp = await fetch(apiUrl('api/auth/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username: username, password: password }),
  });

  let data = null;
  try {
    data = await resp.json();
  } catch (e) {
    data = null;
  }

  if (!resp.ok || !data || !data.access_token) {
    const detail = (data && data.detail) ? data.detail : 'login_error';
    const err = new Error(String(detail));
    err.status = resp.status;
    throw err;
  }

  setTokens(data.access_token, data.refresh_token);
  return makeResponse(resp.status, buildLoginBody(data));
}

/**
 * POST /api/sso/google_login_composer — Google SSO for the composer.
 *
 * LINK-ONLY and STAFF-ONLY server-side (SSO_LINKING_DESIGN.md T3): the id_token
 * resolves through user_third_party_profile, never provisions, and any
 * non-staff resolution gets the same generic 401 the password path returns.
 * On success the response is byte-shaped like login(), so RootPage.loginOK
 * and the T&C gate run unchanged.
 */
export async function loginWithGoogle(idToken) {
  return ssoLogin('api/sso/google_login_composer', { id_token: idToken });
}

/**
 * POST /api/sso/apple_login_composer — Sign in with Apple for the composer.
 *
 * Same rules as Google: link-only, staff-only, one generic 401 for every
 * refusal. `rawNonce` is the UNHASHED value appleIdentity generated for this
 * attempt; the server hashes it and compares against the token's `nonce` claim,
 * which is what makes a captured Apple token unusable on a second request.
 */
export async function loginWithApple(identityToken, rawNonce) {
  return ssoLogin('api/sso/apple_login_composer', {
    identity_token: identityToken,
    raw_nonce: rawNonce,
  });
}

/**
 * POST /api/auth/edconnect/exchange — trade the `ev_code` the EdCity callback
 * put on our return URL for a JWT pair.
 *
 * Unauthenticated, because the code IS the credential: the server minted it
 * seconds ago for a login it drove end to end, it is single-use, and it expires
 * in two minutes. No `udid` is sent — that is the app's device half, and a
 * composer principal must never carry one.
 */
export async function exchangeEdConnectCode(code) {
  return ssoLogin('api/auth/edconnect/exchange', { code: code });
}

/**
 * The one POST-and-adopt-a-session path shared by every SSO provider.
 *
 * Factored out rather than copied three times because the important part is the
 * FAILURE branch: any non-2xx, any missing token, becomes the same opaque
 * Error. Three hand-written copies is how one of them ends up leaking the
 * server's reason to the console and turning the button into an oracle for
 * which identities exist.
 *
 * Wrapped in whileMinting so the guard cannot be forgotten at a call site. The
 * password login() is deliberately NOT wrapped: it is only ever entered from the
 * login screen, which fires no bearer request of its own, so there is nothing in
 * flight for the guard to protect against and no reason to widen the window in
 * which a genuinely dead session survives.
 */
async function ssoLogin(path, payload) {
  return whileMinting(async () => {
    const resp = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    let data = null;
    try {
      data = await resp.json();
    } catch (e) {
      data = null;
    }

    if (!resp.ok || !data || !data.access_token) {
      const err = new Error('login_error');
      err.status = resp.status;
      throw err;
    }

    setTokens(data.access_token, data.refresh_token);
    return makeResponse(resp.status, buildLoginBody(data));
  });
}

/** GET /api/auth/links — the caller's own provider links. */
export async function listLinks() {
  const resp = await authFetch('GET', apiUrl('api/auth/links'));
  let data = null;
  try {
    data = await resp.json();
  } catch (e) {
    data = null;
  }
  if (!resp.ok || !data || !Array.isArray(data.data)) {
    return null;
  }
  return data.data;
}

/**
 * POST /api/auth/link/google — attach a Google identity to the SIGNED-IN
 * account. `password` is the re-auth the server demands for any account that
 * has one (decision 2026-08-13); sending it from here is convenience — the
 * server enforces it either way. Returns {ok, status, body}.
 */
export async function linkGoogle(idToken, password) {
  const payload = { id_token: idToken };
  if (password) {
    payload.password = password;
  }
  const resp = await authFetch('POST', apiUrl('api/auth/link/google'), payload);
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body: body };
}

/**
 * POST /api/auth/link/apple — attach an Apple identity to the SIGNED-IN account.
 * Mirrors linkGoogle, including the password re-auth the server demands of any
 * account that has one.
 */
export async function linkApple(identityToken, rawNonce, password) {
  const payload = { identity_token: identityToken, raw_nonce: rawNonce };
  if (password) {
    payload.password = password;
  }
  const resp = await authFetch('POST', apiUrl('api/auth/link/apple'), payload);
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body: body };
}

/**
 * POST /api/sso/edcity_bind_start — begin linking an EdCity identity.
 *
 * EdConnect is a REDIRECT flow, so it cannot follow the link/google shape: there
 * is no token the browser can hold and hand over. Instead the server records the
 * round trip in its own state and returns the EdCity URL to navigate to. This
 * call carries the bearer; the navigation that follows cannot.
 *
 * TWO credentials, and the server requires both:
 *   - the BEARER, which says which account is asking;
 *   - the PASSWORD, re-proven here exactly as linkGoogle/linkApple have
 *     required since 2026-08-13. Linking is an account takeover primitive if a
 *     walk-up session is enough, and an EdCity link is a full remote sign-in
 *     path afterwards. Accounts with no password (SSO-provisioned) pass without
 *     one, so `password` stays optional on the wire.
 *
 * Returns {authorizeUrl} on success, or {error} — 'password' for a refused
 * re-auth (403), 'failed' for anything else. It used to return a bare URL or
 * null, and null cannot say "your password was wrong", which is the one
 * failure here the user can actually act on.
 */
export async function startEdCityBind(returnUrl, password) {
  // A bind authenticates at EdCity just as a login does, so it leaves the same
  // SSO session behind and must be flagged the same way.
  markEdCitySso();
  const payload = { return_url: returnUrl || window.location.href };
  if (password) {
    payload.password = password;
  }
  const resp = await authFetch('POST', apiUrl('api/sso/edcity_bind_start'), payload);
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  if (resp.status === 403) {
    return { error: 'password' };
  }
  if (!resp.ok || !body || !body.authorize_url) {
    // Pass the server's own code through when it sent one. This used to
    // collapse every non-403 into 'failed', which was harmless while the only
    // refusals were 403s — but bind_start now also answers 409
    // account_already_linked (one EdCity account per account), and flattening
    // that would show the generic "binding failed" instead of the sentence that
    // names the fix. Still falls back to 'failed' for a body with no code, so a
    // genuine network or 5xx failure reads exactly as it did.
    const code = body && typeof body.error === 'string' ? body.error : '';
    return { error: code || 'failed' };
  }
  return { authorizeUrl: body.authorize_url };
}

/**
 * POST /api/sso/edcity_bind_finish — redeem the bind ticket and LINK.
 *
 * This is where the link actually happens, and it is authenticated for a
 * reason. The callback used to perform the write itself, using an account id it
 * read back out of the OAuth state — but nothing ties that state to a browser,
 * so a state minted by one person and completed by another's live EdCity
 * session wrote to the first person's account. Moving the write onto a request
 * that carries a bearer is what makes that impossible: the account is now named
 * by the browser finishing the round trip, not by the one that started it.
 *
 * The ticket is single-use and dies on ANY redemption, including a refused one,
 * so a code lifted off the return URL cannot be walked from account to account.
 *
 * Returns {ok, status, body}; on failure body.error is one of
 * already_linked_to_other / already_linked_created (409), school_mismatch (403),
 * bind_mismatch (403) or bind_expired (410).
 */
export async function finishEdCityBind(bindCode) {
  const resp = await authFetch('POST', apiUrl('api/sso/edcity_bind_finish'), {
    bind_code: bindCode,
  });
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body: body };
}

/** Remember that this browser is about to acquire an EdCity SSO session. */
export function markEdCitySso() {
  try {
    localStorage.setItem(EDCITY_SSO_KEY, '1');
  } catch (e) {
    // Private-browsing quota. The worst case is a logout that does not reach
    // EdCity, which is exactly the behaviour we had before — never a failure
    // that blocks the login the user asked for.
  }
}

/** The marker the EdCity logout return leg lands with. See edCityLogoutUrl. */
export const EDCITY_LOGOUT_PARAM = 'edcity_logout';

/**
 * The URL that ends the EdCity SSO session, or null when this browser has none.
 *
 * Why this exists at all: signing out of the composer clears OUR session, and
 * nothing more. EdCity's own session cookie lives on edconnect2.hkedcity.net,
 * is HttpOnly and cross-origin, so no amount of localStorage clearing here can
 * touch it — and the authorize endpoint looks for an active session BEFORE it
 * shows a login form (spec V1.6 PAGE 3). The next press of the EdConnect button
 * therefore signed the same teacher straight back in with no prompt, which on a
 * shared staffroom PC means signing in as whoever used it last. The server route
 * hands the browser to EdCity's documented logout and brings it back here.
 *
 * DOES NOT CLEAR THE FLAG. It used to, as its first act, and that was the bug:
 * the caller then had a whole teardown plus an untimed revocation POST to get
 * through before it navigated, and if that POST stalled — or the teacher shut
 * the tab once the login card appeared — the browser never reached EdCity. The
 * CAS cookie survived with the flag already gone, so the next press went
 * straight to `edcity_start`, found the live session, and signed the previous
 * teacher back in: exactly the failure this whole mechanism exists to prevent.
 *
 * The flag is now cleared only by [clearEdCitySso], on the RETURN leg, which is
 * the one moment we have positive evidence the session is gone. Every way of
 * not getting there leaves the flag SET, and a stale set flag costs one extra
 * logout hop on the next login — the harmless direction.
 */
export function edCityLogoutUrl(returnUrl) {
  let flagged = false;
  try {
    flagged = localStorage.getItem(EDCITY_SSO_KEY) === '1';
  } catch (e) {
    flagged = false;
  }
  if (!flagged) {
    return null;
  }
  const base = returnUrl || (window.location.origin + window.location.pathname);
  // The marker is what tells the landing app the round trip completed. Appended
  // to the RETURN url, so it survives EdCity's redirect back to us.
  const target = base + (base.indexOf('?') >= 0 ? '&' : '?')
    + EDCITY_LOGOUT_PARAM + '=1';
  return apiUrl('api/sso/edcity_logout')
    + '?flow=composer&return=' + encodeURIComponent(target);
}

/**
 * Forget that this browser holds an EdCity SSO session.
 *
 * Called ONLY from the return leg of the logout round trip — landing back on
 * our own origin after EdCity's logout is the evidence that the CAS session is
 * actually gone. Anywhere else and we are guessing.
 */
export function clearEdCitySso() {
  try {
    localStorage.removeItem(EDCITY_SSO_KEY);
  } catch (e) {
    // Private browsing. The flag was never stored, so there is nothing to drop.
  }
}

/**
 * The URL that starts an EdCity LOGIN. A plain top-level navigation target, not
 * something to fetch: the browser must follow the 302 to EdCity's own login
 * page, and a cross-origin fetch would consume the redirect itself.
 *
 * Also flags the browser as holding an EdCity SSO session from here on. The
 * marking lives in here rather than at the call site so that it cannot be
 * forgotten by a future caller: producing this URL and navigating to it are the
 * same act, and the session it leaves behind is what logout has to undo.
 *
 * A SECOND press (the flag is already set) does not go straight to authorize.
 * EdCity looks up an active CAS session before it renders a login form
 * (spec V1.6 PAGE 3), so a student who was just refused would be signed back
 * in silently and Composer would bounce them to the same error — the teacher
 * login page never appears. `then=login` logs that session out, then starts a
 * fresh authorize so the password form actually opens.
 */
export function edCityLoginUrl(returnUrl) {
  const target = returnUrl || (window.location.origin + window.location.pathname);
  let alreadyHadSso = false;
  try {
    alreadyHadSso = localStorage.getItem(EDCITY_SSO_KEY) === '1';
  } catch (e) {
    alreadyHadSso = false;
  }
  markEdCitySso();
  if (alreadyHadSso) {
    return apiUrl('api/sso/edcity_logout')
      + '?flow=composer&then=login&return=' + encodeURIComponent(target);
  }
  return apiUrl('api/sso/edcity_start')
    + '?flow=composer&return=' + encodeURIComponent(target);
}

/**
 * POST /api/sso/edcity_pending_info — describe a pending EdCity sign-in.
 *
 * When the EdCity callback finds an UNLINKED identity in the composer flow it
 * no longer refuses outright: it parks the identity in a single-use pending
 * ticket and returns only the ticket's opaque code on the redirect. This call
 * PEEKS at the ticket — it is not consumed — so the choice dialog can render a
 * name and school before the user commits to anything. Unauthenticated: the
 * code is the credential, exactly as with the ev_code exchange.
 * Returns {ok, status, body}; a 410 body carries {error: 'pending_expired'}.
 */
export async function getEdCityPendingInfo(pendCode) {
  const resp = await fetch(apiUrl('api/sso/edcity_pending_info'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pend_code: pendCode }),
  });
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body: body };
}

/**
 * POST /api/sso/edcity_link_after_login — attach the pending EdCity identity
 * to the account that just signed in with username/password.
 *
 * Bearer-authed: the token names WHICH account the link attaches to, the
 * ticket names WHICH identity, and the server consumes the ticket whatever the
 * outcome. Returns {ok, status, body}; on failure body.error is one of
 * school_mismatch (403), identity_conflict (409, with created_account), or
 * pending_expired (410).
 */
export async function linkEdCityPending(pendCode) {
  const resp = await authFetch('POST', apiUrl('api/sso/edcity_link_after_login'), {
    pend_code: pendCode,
  });
  let body = null;
  try {
    body = await resp.json();
  } catch (e) {
    body = null;
  }
  return { ok: resp.ok, status: resp.status, body: body };
}

/**
 * POST /api/sso/edcity_provision — create a NEW account from the pending
 * EdCity identity, after the user explicitly confirmed.
 *
 * Unauthenticated (the ticket is the credential) and staff-only server-side.
 * Success returns the same token body as the ev_code exchange, so the session
 * is adopted the same way. Unlike ssoLogin's deliberately opaque failure, the
 * thrown error carries the server's `error` code: not_eligible and
 * pending_expired are defined for a caller who has already proven this exact
 * identity at CAS, so surfacing them leaks nothing new.
 */
export async function provisionFromEdCity(pendCode) {
  return whileMinting(async () => {
    const resp = await fetch(apiUrl('api/sso/edcity_provision'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pend_code: pendCode }),
    });

    let data = null;
    try {
      data = await resp.json();
    } catch (e) {
      data = null;
    }

    if (!resp.ok || !data || !data.access_token) {
      const err = new Error('login_error');
      err.status = resp.status;
      err.code = (data && data.error) ? String(data.error) : null;
      throw err;
    }

    setTokens(data.access_token, data.refresh_token);
    return makeResponse(resp.status, buildLoginBody(data));
  });
}

/** DELETE /api/auth/link/{id} — soft-unbind one of the caller's own links. */
export async function unlinkAccount(linkId) {
  const resp = await authFetch('DELETE', apiUrl('api/auth/link/' + Number(linkId)));
  return resp.ok;
}

/** GET /api/auth/session — used by the EdConnect return leg and manual restore. */
export async function fetchSession() {
  const resp = await authFetch('GET', apiUrl('api/auth/session'));
  let data = null;
  try {
    data = await resp.json();
  } catch (e) {
    data = null;
  }
  if (!resp.ok || !data) {
    return null;
  }
  return buildLoginBody(data);
}

export async function logout() {
  const rt = getRefreshToken();
  try {
    await authFetch(
      'POST',
      apiUrl('api/auth/logout'),
      // Sending the refresh token revokes it too; without it the 30-day token
      // survives a logout and could still mint access tokens.
      rt ? { refresh_token: rt } : {},
    );
  } catch (e) {
    // A failed logout must never trap the user in the app.
    console.warn('AuthService: logout request failed', e);
  }
  clearSession();
}

/**
 * [logout], but give up waiting after `ms` and clear the local session anyway.
 *
 * `authFetch` has no timeout, so a stalled revocation POST — a captive portal, a
 * half-open TCP connection — hangs this promise indefinitely. That matters only
 * because of what waits BEHIND it: TopBar queues the hop to EdCity's logout
 * after this resolves, and that hop is what stops the next person at a shared
 * machine being signed in as the last one.
 *
 * So the two failures are ranked, not balanced. Not revoking the refresh token
 * leaves a credential that expires on its own; not ending the CAS session hands
 * over an account. The request is left running rather than aborted — it may
 * still land and revoke — and the local session is dropped either way, so the
 * user is signed out here regardless of what the network did.
 */
export async function logoutWithin(ms) {
  let timer = null;
  try {
    await Promise.race([
      logout(),
      new Promise((resolve) => { timer = setTimeout(resolve, ms); }),
    ]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
    // logout() clears on its own path; repeating it covers the timeout branch,
    // where logout() is still in flight and has not reached its clearSession().
    clearSession();
  }
}

// refreshAccessToken() outcomes. The caller has to tell "the refresh token is
// dead" apart from "the refresh call did not complete": only the first one may
// destroy the session, and only the first one means the user has to sign in
// again. A 5xx from the API — an ACA revision restart, a MySQL blip — used to
// take the same path as a rejection and wipe a perfectly valid 30-day refresh
// token, so a two-second outage cost the user their session and any unsaved
// editing state.
export const REFRESH_OK = 'ok';
export const REFRESH_REJECTED = 'rejected';
export const REFRESH_TRANSIENT = 'transient';

/**
 * Single-flight refresh: N parallel 401s produce exactly ONE POST
 * /api/auth/refresh, and every waiter resolves off the same promise.
 * Resolves to one of the REFRESH_* outcomes above.
 */
export function refreshAccessToken() {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const rt = getRefreshToken();
  if (!rt) {
    endSession();
    return Promise.resolve(REFRESH_REJECTED);
  }

  refreshInFlight = fetch(apiUrl('api/auth/refresh'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
  })
    .then((resp) => {
      if (resp.ok) {
        return resp.json().then((data) => {
          if (data && data.access_token) {
            setTokens(data.access_token, null);
            return REFRESH_OK;
          }
          // 2xx with no token is a broken contract, not a valid session.
          return REFRESH_REJECTED;
        });
      }
      // ONLY the server explicitly refusing the refresh token means the
      // session is over. Anything else (500/502/503/504, 429, a proxy error)
      // is transient: keep the tokens so the next attempt — or a reload — can
      // recover.
      return (resp.status === 401 || resp.status === 403)
        ? REFRESH_REJECTED
        : REFRESH_TRANSIENT;
    })
    .catch(() => {
      // A network blip must not destroy a valid session.
      return REFRESH_TRANSIENT;
    })
    .then((result) => {
      if (result === REFRESH_REJECTED) {
        endSession();
      }
      refreshInFlight = null;
      return result;
    });

  return refreshInFlight;
}

/**
 * fetch() with the bearer attached. On 401 it refreshes ONCE and retries the
 * same request; the retry never refreshes again.
 */
export async function authFetch(method, url, body = undefined, allowRetry = true) {
  const headers = {};
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = 'Bearer ' + token;
  }
  const init = { method: method, headers: headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const resp = await fetch(url, init);
  if (resp.status === 401 && allowRetry) {
    if (getRefreshToken()) {
      const outcome = await refreshAccessToken();
      if (outcome === REFRESH_OK) {
        return authFetch(method, url, body, false);
      }
      if (outcome === REFRESH_TRANSIENT) {
        // The session is still valid — the refresh call just did not get
        // through. Surface this as an ordinary request failure instead of
        // throwing the user back to the login screen and discarding whatever
        // they had open.
        return resp;
      }
      // REFRESH_REJECTED: refreshAccessToken() already cleared and notified.
      return resp;
    }
    // No refresh token at all — e.g. a pre-migration 'userdata' blob that has
    // a loginResponse but no JWT pair, or the session RootPage drops just before
    // redeeming an EdConnect code. Nothing can rescue this session.
    endSession();
  }
  return resp;
}

const AuthService = {
  apiUrl,
  authFetch,
  clearSession,
  edCityLogoutUrl,
  clearEdCitySso,
  EDCITY_LOGOUT_PARAM,
  edCityLoginUrl,
  exchangeEdConnectCode,
  fetchSession,
  getAccessToken,
  getEdCityPendingInfo,
  getRefreshToken,
  isLoggedIn,
  linkApple,
  linkEdCityPending,
  linkGoogle,
  listLinks,
  login,
  loginWithApple,
  loginWithGoogle,
  logout,
  logoutWithin,
  markEdCitySso,
  startEdCityBind,
  finishEdCityBind,
  makeResponse,
  persistSession,
  provisionFromEdCity,
  refreshAccessToken,
  setSessionExpiredHandler,
  setTokens,
  unlinkAccount,
};

export default AuthService;
